// @ts-strict-ignore
import React, {
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import {
  Box,
  useSnackBar,
} from '@usealloy/component-library';

import Collapsibox from '~/components/Collapsibox';
import { UserContext } from '~/dashboard/context';
import { useInitializeWorkflowRedux } from '~/dashboard/views/workflows/editor/hooks/useInitializeWorkflowRedux';
import { Col, Row } from '~/entities/evaluations/EventTypeEvaluations/common';
import TagsList from '~/entities/evaluations/TagsList';
import TransactionHistoryCollapsibox from '~/entities/evaluations/transaction/history/TransactionHistoryCollapsibox';
import type { Entity } from '~/entities/types';
import { ajaxFetch, buildURLParamsString } from '~/helpers/api';
import { routes } from '~/helpers/routes';

import { PortfolioEvaluationBankAccounts } from './PortfolioEvaluationBankAccounts';
import type { Dimension } from '../redux/types';

type Props = {
  entity: Entity;
  outcome: string;
  tags: any[];
  workflowToken: string;
  workflowVersion: number;
  portfolioEvaluationToken: string;
  dimensions?: Dimension[];
};

export const PortfolioEvaluationContent = ({
  entity,
  outcome,
  portfolioEvaluationToken,
  tags,
  workflowToken,
  workflowVersion,
  dimensions,
}: Props) => {
  const addSnackBar = useSnackBar();
  const { agent, featureFlags } = useContext(UserContext);
  const [bankAccountData, setBankAccountData] = useState(undefined);

  const getBankAccountData = useCallback(async () => {
    const params = buildURLParamsString({
      entity_token: entity.entity_token,
      num_days: 30,
    });
    const url = `${routes.transactions.accountInfo}${params}`;
    try {
      const accountSummaries = await ajaxFetch(url);
      setBankAccountData(accountSummaries?.data);
    } catch (exception) {
      addSnackBar({
        duration: 2_000,
        id: 'sb-fetch-accountinfo-failure',
        title: 'Something went wrong while getting the account information.',
        type: 'danger',
      });
    }
  }, [
    addSnackBar,
    entity.entity_token,
  ]);

  useEffect(() => { getBankAccountData(); }, [getBankAccountData]);

  const internalBankAccounts = (bankAccountData ?? [])
    .filter(data => !data.bank_account.is_linked_account);
  const externalBankAccounts = (bankAccountData ?? [])
    .filter(data => data.bank_account.is_linked_account);

  useInitializeWorkflowRedux({
    workflowToken,
    versionId: workflowVersion,
    featureFlags,
  });

  return (
    <Box
      direction="column"
      gap={5}
    >
      <Row key="overview">
        <Col>
          <Collapsibox
            header="Accounts"
          >
            <Box direction="column" gap={3}>
              <PortfolioEvaluationBankAccounts accounts={internalBankAccounts} />
            </Box>
          </Collapsibox>

          <Collapsibox
            header="External Linked Accounts"
          >
            <Box direction="column" gap={3}>
              <PortfolioEvaluationBankAccounts accounts={externalBankAccounts} />
            </Box>
          </Collapsibox>
        </Col>

        <Collapsibox
          header="Tags"
          collapsible
        >
          <TagsList
            tags={tags ?? []}
            outcome={outcome}
            evaluationToken={portfolioEvaluationToken}
            evaluationTags={tags ?? []}
            entityToken={entity.entity_token}
            isPortfolioEvaluation
            dimensions={dimensions}
            workflowToken={workflowToken}
            workflowVersion={workflowVersion}
          />
        </Collapsibox>
      </Row>
      <Row key="activity">
        <TransactionHistoryCollapsibox
          header="Recent Transaction Activity"
          view="evaluation"
          entity={entity}
          agent={agent || {}}
        />
      </Row>
    </Box>
  );
};
